import { useCallback, useEffect, useState } from 'react';
import { stores as storesApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can, Pagination, money } from '../components/Common';
import LocationPicker from '../components/LocationPicker';

export default function Stores() {
  const toast = useToast();

  const [data, setData] = useState({ stores: [], meta: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await storesApi.list({ page, limit: 20, search: search || undefined });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load stores', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Stores · UrbanClean'; }, []);

  const toggle = async (store) => {
    try {
      const res = await storesApi.toggle(store._id);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error('Could not update', err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Stores</h1>
          <div className="sub">Branches, service areas and GST details</div>
        </div>
        <Can permission="store.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New store</button>
        </Can>
      </div>

      <div className="toolbar">
        <input className="input" placeholder="Search name, code or city" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ minWidth: 240 }} />
      </div>

      <div className="card">
        {loading ? <Loading /> : data.stores.length === 0 ? (
          <Empty title="No stores yet" body="Add your first branch to start routing orders." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Store</th><th>Contact</th><th>Service pincodes</th>
                    <th className="center">Orders</th><th className="right">Revenue</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.stores.map((s) => (
                    <tr key={s._id}>
                      <td>
                        <div className="strong">{s.name}</div>
                        <div className="muted mono" style={{ fontSize: 12 }}>{s.code}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{s.city}</div>
                      </td>
                      <td className="muted">
                        {s.mobile}
                        <div style={{ fontSize: 12 }}>{s.email}</div>
                      </td>
                      <td className="muted" style={{ maxWidth: 210, fontSize: 12 }}>
                        {s.serviceablePincodes?.length ? s.serviceablePincodes.join(', ') : '—'}
                      </td>
                      <td className="center">{s.totalOrders}</td>
                      <td className="right strong">{money(s.totalRevenue)}</td>
                      <td><span className={`badge badge-${s.isActive ? 'green' : 'red'}`}>{s.isActive ? 'Active' : 'Closed'}</span></td>
                      <td className="right nowrap">
                        <Can permission="store.update">
                          <button className="btn btn-sm btn-ghost" onClick={() => setEditing(s)}>Edit</button>
                          <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6 }} onClick={() => toggle(s)}>
                            {s.isActive ? 'Close' : 'Open'}
                          </button>
                        </Can>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data.meta} onPage={setPage} />
          </>
        )}
      </div>

      <StoreEditor store={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </>
  );
}

function StoreEditor({ store, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!store) return;
    setForm({
      name: store.name || '', ownerName: store.ownerName || '',
      mobile: store.mobile || '', email: store.email || '',
      address: store.address || '', city: store.city || '', state: store.state || '',
      pincode: store.pincode || '', gstNumber: store.gstNumber || '',
      openingTime: store.openingTime || '09:00', closingTime: store.closingTime || '21:00',
      lat: store.lat ?? '', lng: store.lng ?? '',
      serviceablePincodes: (store.serviceablePincodes || []).join(', '),
    });
  }, [store]);

  if (!store) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.name) return toast.error('Give the store a name');
    setBusy(true);
    try {
      const body = {
        ...form,
        lat: form.lat === '' ? null : Number(form.lat),
        lng: form.lng === '' ? null : Number(form.lng),
        serviceablePincodes: form.serviceablePincodes.split(',').map((p) => p.trim()).filter(Boolean),
      };
      if (store._id) await storesApi.update(store._id, body);
      else await storesApi.create(body);
      toast.success('Store saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open wide title={store._id ? `Edit — ${store.name}` : 'New store'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save store'}</button>
      </>}>
      <div className="row">
        <div className="field"><label>Store name *</label><input className="input" value={form.name || ''} onChange={set('name')} /></div>
        <div className="field"><label>Owner / manager</label><input className="input" value={form.ownerName || ''} onChange={set('ownerName')} /></div>
      </div>
      <div className="row">
        <div className="field"><label>Mobile</label><input className="input" value={form.mobile || ''} onChange={set('mobile')} /></div>
        <div className="field"><label>Email</label><input className="input" value={form.email || ''} onChange={set('email')} /></div>
      </div>
      <div className="field"><label>Address</label><input className="input" value={form.address || ''} onChange={set('address')} /></div>
      <div className="row">
        <div className="field"><label>City</label><input className="input" value={form.city || ''} onChange={set('city')} /></div>
        <div className="field"><label>State</label><input className="input" value={form.state || ''} onChange={set('state')} /></div>
        <div className="field"><label>Pincode</label><input className="input" value={form.pincode || ''} onChange={set('pincode')} /></div>
      </div>
      <div className="field">
        <label>Serviceable pincodes</label>
        <input className="input" value={form.serviceablePincodes || ''} onChange={set('serviceablePincodes')} placeholder="110034, 110052, 110088" />
        <div className="hint">Comma separated. App orders from these pincodes route to this store automatically.</div>
      </div>
      <div className="row">
        <div className="field"><label>GST number</label><input className="input" value={form.gstNumber || ''} onChange={set('gstNumber')} /></div>
        <div className="field"><label>Opens</label><input className="input" type="time" value={form.openingTime || ''} onChange={set('openingTime')} /></div>
        <div className="field"><label>Closes</label><input className="input" type="time" value={form.closingTime || ''} onChange={set('closingTime')} /></div>
      </div>
      {/* Search, drop a pin, or paste a Google Maps link - all three fill in
          the coordinates below. */}
      <LocationPicker
        lat={form.lat}
        lng={form.lng}
        address={form.address}
        onChange={({ lat, lng, address }) =>
          setForm((f) => ({
            ...f,
            lat,
            lng,
            // Only auto-fill the address fields that are still blank, so a
            // hand-typed address is never overwritten.
            address: f.address || address || '',
          }))
        }
      />
    </Modal>
  );
}
